import { getReferralId } from './helpers';

/**
 * Prepares all form options from API response data
 */
export function prepareFormOptions(apiData) {
  if (!apiData || !apiData.data) {
    return null;
  }

  const { data } = apiData;

  return {
    // Categories with proper mapping and ordering
    categories: mapCategoriesWithOrder(data.categories || {}),

    // Team sizes with proper mapping
    sizes: mapSizes(data.sizes || {}),

    // Previous systems with proper mapping
    previousSystems: mapPreviousSystems(data.previous_systems || {}),

    // Countries with proper structure
    countries: data.countries || [],

    // Currencies with proper structure
    currencies: createUniqueCurrencies(data.countries || []),

    // Timezones with proper sorting
    timeZones: sortTimezonesByGMT(
      Object.entries(data.time_zones || {}).map(([key, value]) => ({
        value: key,
        label: value,
      })),
    ),

    referralId: getReferralId(), // Referral ID from session storage or URL parameters

    // Raw data for reference
    rawData: data,
  };
}

/**
 * Maps categories with proper ordering and naming
 * Returns an array of [key, value] pairs in the correct display order
 */
function mapCategoriesWithOrder(originalCategories = {}) {
  const categoryNameMap = {
    1: 'Other',
    2: 'Hair',
    3: 'Beauty',
    4: 'Massage & spa',
    5: 'Group Fitness',
    6: 'Wellbeing',
    7: 'Tutoring & consulting',
    12: 'Hair & beauty',
    13: 'Barber',
    14: 'Body art',
    15: 'Personal trainer',
  };

  const mappedCategories = {};

  // Map categories with proper names
  Object.entries(originalCategories).forEach(([key, value]) => {
    mappedCategories[key] = categoryNameMap[key] || value;
  });

  const CATEGORY_ORDER = [3, 12, 2, 6, 4, 13, 14, 15, 7, 1];

  // Return ordered categories as array of [key, value] pairs
  return CATEGORY_ORDER.reduce((ordered, key) => {
    if (mappedCategories[key]) {
      ordered.push([key, mappedCategories[key]]);
    }
    return ordered;
  }, []);
}

/**
 * Maps team sizes with proper naming and ordering
 * Returns an array of [key, value] pairs in logical order
 */
function mapSizes(originalSizes = {}) {
  const sizeNameMap = {
    1: 'Just me',
    2: '2-7 staff',
    3: '8-20 staff',
    4: 'Over 20 staff',
  };

  const SIZE_ORDER = [1, 2, 3, 4];

  return SIZE_ORDER.reduce((ordered, key) => {
    if (originalSizes[key]) {
      ordered.push([key, sizeNameMap[key] || originalSizes[key]]);
    }
    return ordered;
  }, []);
}

/**
 * Maps previous systems with proper naming and ordering
 * Returns an array of [key, value] pairs in logical order
 */
function mapPreviousSystems(originalSystems = {}) {
  const systemNameMap = {
    1: 'Pen and paper',
    2: 'Phorest',
    3: 'Fresha',
    4: 'Shortcuts',
    5: 'Kitomba',
    6: 'Walk-ins',
    7: 'Other software',
    8: "I'm a new business",
  };

  const SYSTEM_ORDER = [3, 2, 4, 5, 7];

  return SYSTEM_ORDER.reduce((ordered, key) => {
    if (originalSystems[key]) {
      ordered.push([key, systemNameMap[key] || originalSystems[key]]);
    }
    return ordered;
  }, []);
}

/**
 * Creates unique currencies from countries data
 */
function createUniqueCurrencies(countries = []) {
  const seenCurrencies = new Set();

  return countries
    .filter((country) => {
      if (seenCurrencies.has(country.currency_code)) return false;
      seenCurrencies.add(country.currency_code);
      return true;
    })
    .map((country) => ({
      Currency_code: country.currency_code,
      Currency_name: country.currency_name,
      Currency_long_name: `(${country.currency_code}) ${country.currency_name}`,
    }));
}

/**
 * Sorts timezones by GMT offset, then alphabetically
 */
function sortTimezonesByGMT(timeZones) {
  return timeZones.sort((a, b) => {
    const getGMTOffset = (label) => {
      const match = label.match(/GMT([+-]?\d+(?::\d{2})?)/);
      if (!match) return 0;

      const parts = match[1].split(':');
      const hours = parseInt(parts[0]);
      const minutes = parts[1] ? parseInt(parts[1]) / 60 : 0;
      return hours >= 0 ? hours + minutes : hours - minutes;
    };

    const offsetA = getGMTOffset(a.label);
    const offsetB = getGMTOffset(b.label);

    return offsetA - offsetB || a.label.localeCompare(b.label);
  });
}
